import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { ToastContainer } from 'react-toastify';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import HomeRoute from './components/HomeRoute';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import BackupProgress from './components/BackupProgress';
import Settings from './components/Settings';
import Privacy from './components/Privacy';
import Terms from './components/Terms';
import Pricing from './components/Pricing';
import HelpCenter from './components/HelpCenter';
import Contact from './components/Contact';
import Status from './components/Status';
import SimplePage from './components/SimplePage';
import AdminDashboard from './pages/AdminDashboard';
import AffiliatesLanding from './pages/AffiliatesLanding';
import AffiliateDashboard from './pages/AffiliateDashboard';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import OAuthCallback from './components/OAuthCallback';
import RouteTracker from './components/RouteTracker';
import ReferralRedirect from './components/ReferralRedirect';
import AffiliateRedirect from './components/AffiliateRedirect';
import ChatWidget from './components/ChatWidget';
import InstagramPersonalAccount from './components/InstagramPersonalAccount';
import ContentRestore from './components/ContentRestore';
import LandingPage from './components/LandingPage';
import CommunityJoin from './components/CommunityJoin';
import AudienceManager from './components/AudienceManager';
import 'react-toastify/dist/ReactToastify.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router>
          <RouteTracker />
          <div className="App">
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<HomeRoute />} />
              <Route path="/landing" element={<LandingPage />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/ref/:code" element={<ReferralRedirect />} />
              <Route path="/aff/:code" element={<AffiliateRedirect />} />
              <Route path="/affiliates" element={<AffiliatesLanding />} />
              <Route path="/affiliate-dashboard" element={
                <ProtectedRoute>
                  <Layout>
                    <AffiliateDashboard />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              
              {/* Support pages */}
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/instagram-personal-account" element={<InstagramPersonalAccount />} />
              <Route path="/status" element={<Status />} />
              <Route path="/docs" element={
                <SimplePage title="API Documentation" subtitle="Complete API reference for developers">
                  <div className="prose prose-invert max-w-none">
                    <p className="text-gray-300 text-lg mb-6">
                      Our API documentation provides comprehensive information for integrating with Vaulto services.
                    </p>
                    <div className="bg-white/5 rounded-xl p-6 mb-6">
                      <h3 className="text-xl font-semibold text-white mb-4">Getting Started</h3>
                      <p className="text-gray-300">
                        To access the Vaulto API, you'll need an API key. You can generate one from your dashboard settings.
                      </p>
                    </div>
                    <div className="bg-white/5 rounded-xl p-6 mb-6">
                      <h3 className="text-xl font-semibold text-white mb-4">Base URL</h3>
                      <code className="text-pink-400 bg-white/10 px-3 py-1 rounded">https://api.vaulto.social/v1</code>
                    </div>
                    <div className="text-center mt-8">
                      <a href="/dashboard" className="text-pink-400 hover:text-pink-300 font-medium">
                        View Full Documentation →
                      </a>
                    </div>
                  </div>
                </SimplePage>
              } />
              
              {/* Legal pages */}
              <Route path="/data-deletion" element={
                <SimplePage title="Data Deletion Instructions" subtitle="How to request deletion of your data">
                  <div className="prose prose-invert max-w-none">
                    <p className="text-gray-300 text-lg mb-6">
                      We respect your right to control your personal data. This page explains how you can request the deletion of your data from Vaulto.
                    </p>
                    
                    <div className="bg-white/5 rounded-xl p-6 mb-6">
                      <h3 className="text-xl font-semibold text-white mb-4">How to Request Data Deletion</h3>
                      <p className="text-gray-300 mb-4">
                        You can request deletion of your data in the following ways:
                      </p>
                      <ol className="list-decimal list-inside text-gray-300 space-y-2 mb-4">
                        <li><strong>By Email:</strong> Send a data deletion request to <a href="mailto:admin@vaulto.social" className="text-pink-400 hover:text-pink-300">admin@vaulto.social</a></li>
                        <li><strong>Via Instagram:</strong> Disconnect Vaulto from your Instagram account settings</li>
                      </ol>
                    </div>
                    
                    <div className="bg-white/5 rounded-xl p-6 mb-6">
                      <h3 className="text-xl font-semibold text-white mb-4">What Data Will Be Deleted</h3>
                      <p className="text-gray-300 mb-4">
                        When you request data deletion, we will remove:
                      </p>
                      <ul className="list-disc list-inside text-gray-300 space-y-2">
                        <li>Your account information (email, name, profile data)</li>
                        <li>Your Instagram connection and access tokens</li>
                        <li>Your Google Drive connection and access tokens</li>
                        <li>Backup history and metadata</li>
                        <li>Any cached Instagram content in our temporary storage</li>
                        <li>All backup schedules and preferences</li>
                      </ul>
                    </div>
                    
                    <div className="bg-white/5 rounded-xl p-6 mb-6">
                      <h3 className="text-xl font-semibold text-white mb-4">Important Notes</h3>
                      <ul className="list-disc list-inside text-gray-300 space-y-2">
                        <li>Content already backed up to your Google Drive will NOT be deleted (it's your data in your account)</li>
                        <li>We may retain certain data as required by law or for legitimate business purposes</li>
                        <li>Data deletion is permanent and cannot be undone</li>
                        <li>You will need to create a new account if you wish to use Vaulto again</li>
                      </ul>
                    </div>
                    
                    <div className="bg-white/5 rounded-xl p-6 mb-6">
                      <h3 className="text-xl font-semibold text-white mb-4">Deletion Timeline</h3>
                      <p className="text-gray-300">
                        <strong>Immediate:</strong> Your account will be deactivated immediately upon request<br/>
                        <strong>Within 48 hours:</strong> All your data will be permanently deleted from our systems<br/>
                        <strong>Within 30 days:</strong> All backups and logs containing your data will be purged
                      </p>
                    </div>
                    
                    <div className="bg-white/5 rounded-xl p-6 mb-6">
                      <h3 className="text-xl font-semibold text-white mb-4">Disconnecting Instagram Without Data Deletion</h3>
                      <p className="text-gray-300 mb-4">
                        If you only want to disconnect your Instagram account without deleting all your Vaulto data:
                      </p>
                      <ol className="list-decimal list-inside text-gray-300 space-y-2">
                        <li>Go to your Instagram account settings</li>
                        <li>Navigate to "Apps and Websites"</li>
                        <li>Find Vaulto and click "Remove"</li>
                        <li>This will revoke Vaulto's access to your Instagram data</li>
                      </ol>
                    </div>
                    
                    <div className="bg-pink-500/10 border border-pink-500/30 rounded-xl p-6 mb-6">
                      <h3 className="text-xl font-semibold text-white mb-4">Need Help?</h3>
                      <p className="text-gray-300 mb-4">
                        If you have any questions about data deletion or need assistance, please contact us:
                      </p>
                      <p className="text-gray-300">
                        <strong>Email:</strong> <a href="mailto:admin@vaulto.social" className="text-pink-400 hover:text-pink-300">admin@vaulto.social</a><br/>
                        <strong>Response time:</strong> Within 24 hours
                      </p>
                    </div>
                    
                    <div className="text-sm text-gray-400 mt-8 pt-4 border-t border-white/20">
                      <p>Last updated: {new Date().toLocaleDateString()}</p>
                    </div>
                  </div>
                </SimplePage>
              } />
              
              <Route path="/cookies" element={
                <SimplePage title="Cookie Policy" subtitle="How we use cookies on Vaulto">
                  <div className="prose prose-invert max-w-none">
                    <p className="text-gray-300 text-lg mb-6">
                      This Cookie Policy explains how Vaulto uses cookies and similar technologies.
                    </p>
                    <h3 className="text-xl font-semibold text-white mb-4">What are cookies?</h3>
                    <p className="text-gray-300 mb-6">
                      Cookies are small text files that are placed on your device when you visit our website.
                    </p>
                    <h3 className="text-xl font-semibold text-white mb-4">How we use cookies</h3>
                    <ul className="list-disc list-inside text-gray-300 space-y-2 mb-6">
                      <li>Essential cookies for site functionality</li>
                      <li>Analytics cookies to understand usage</li>
                      <li>Preference cookies to remember your settings</li>
                    </ul>
                    <h3 className="text-xl font-semibold text-white mb-4">Managing cookies</h3>
                    <p className="text-gray-300">
                      You can control cookies through your browser settings. Note that disabling cookies may affect site functionality.
                    </p>
                  </div>
                </SimplePage>
              } />
              
              <Route path="/security" element={
                <SimplePage title="Security" subtitle="How we keep your data safe">
                  <div className="prose prose-invert max-w-none">
                    <p className="text-gray-300 text-lg mb-6">
                      At Vaulto, we take the security of your data seriously. Here's how we protect your information.
                    </p>
                    <div className="space-y-6">
                      <div className="bg-white/5 rounded-xl p-6">
                        <h3 className="text-xl font-semibold text-white mb-3">🔐 End-to-End Encryption</h3>
                        <p className="text-gray-300">
                          All data transfers between your Instagram account and Google Drive are encrypted using industry-standard protocols.
                        </p>
                      </div>
                      <div className="bg-white/5 rounded-xl p-6">
                        <h3 className="text-xl font-semibold text-white mb-3">🛡️ Zero Storage Policy</h3>
                        <p className="text-gray-300">
                          We don't store your Instagram content on our servers. Everything goes directly to your Google Drive.
                        </p>
                      </div>
                      <div className="bg-white/5 rounded-xl p-6">
                        <h3 className="text-xl font-semibold text-white mb-3">🔍 Regular Security Audits</h3>
                        <p className="text-gray-300">
                          Our infrastructure undergoes regular security audits and penetration testing.
                        </p>
                      </div>
                    </div>
                  </div>
                </SimplePage>
              } />
              
              {/* Company pages */}
              <Route path="/about" element={
                <SimplePage title="About Vaulto" subtitle="Our mission to protect your digital memories">
                  <div className="prose prose-invert max-w-none">
                    <p className="text-gray-300 text-lg mb-6">
                      Vaulto was founded with a simple mission: to help content creators protect their digital legacy.
                    </p>
                    <h3 className="text-xl font-semibold text-white mb-4">Our Story</h3>
                    <p className="text-gray-300 mb-6">
                      After seeing countless creators lose their content due to account issues, we built Vaulto to provide automatic, secure backups of Instagram content.
                    </p>
                    <h3 className="text-xl font-semibold text-white mb-4">Our Values</h3>
                    <ul className="list-disc list-inside text-gray-300 space-y-2 mb-6">
                      <li>Privacy first - Your content is yours alone</li>
                      <li>Reliability - Automated backups you can count on</li>
                      <li>Simplicity - Easy to use for everyone</li>
                      <li>Transparency - Clear about what we do and how</li>
                    </ul>
                    <div className="text-center mt-8">
                      <a href="/contact" className="text-pink-400 hover:text-pink-300 font-medium">
                        Get in touch →
                      </a>
                    </div>
                  </div>
                </SimplePage>
              } />
              
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              
              <Route path="/careers" element={
                <SimplePage title="Careers" subtitle="Join our team and help protect digital memories">
                  <div className="prose prose-invert max-w-none">
                    <p className="text-gray-300 text-lg mb-8">
                      We're always looking for talented individuals who share our passion for helping creators.
                    </p>
                    <div className="bg-white/5 rounded-xl p-6 text-center">
                      <h3 className="text-xl font-semibold text-white mb-4">No Open Positions</h3>
                      <p className="text-gray-300 mb-6">
                        We don't have any open positions at the moment, but we'd love to hear from you!
                      </p>
                      <a href="mailto:careers@vaulto.social" className="text-pink-400 hover:text-pink-300 font-medium">
                        Send us your resume →
                      </a>
                    </div>
                  </div>
                </SimplePage>
              } />
              
              <Route path="/press" element={
                <SimplePage title="Press" subtitle="Media resources and press inquiries">
                  <div className="prose prose-invert max-w-none">
                    <p className="text-gray-300 text-lg mb-8">
                      For press inquiries, media resources, and company information.
                    </p>
                    <div className="grid md:grid-cols-2 gap-6 mb-8">
                      <div className="bg-white/5 rounded-xl p-6">
                        <h3 className="text-xl font-semibold text-white mb-3">Press Kit</h3>
                        <p className="text-gray-300 mb-4">
                          Download our press kit with logos, screenshots, and company information.
                        </p>
                        <button className="text-pink-400 hover:text-pink-300 font-medium">
                          Download Press Kit →
                        </button>
                      </div>
                      <div className="bg-white/5 rounded-xl p-6">
                        <h3 className="text-xl font-semibold text-white mb-3">Media Contact</h3>
                        <p className="text-gray-300 mb-4">
                          For press inquiries and media requests.
                        </p>
                        <a href="mailto:press@vaulto.social" className="text-pink-400 hover:text-pink-300 font-medium">
                          press@vaulto.social →
                        </a>
                      </div>
                    </div>
                  </div>
                </SimplePage>
              } />
              
              {/* Community join page - public, no auth required */}
              <Route path="/c/:slug" element={<CommunityJoin />} />
              
              {/* OAuth callback routes */}
              <Route path="/oauth/instagram/callback" element={<OAuthCallback />} />
              <Route path="/oauth/google/callback" element={<OAuthCallback />} />
              <Route path="/oauth/facebook/callback" element={<OAuthCallback />} />
              <Route path="/oauth-callback" element={<OAuthCallback />} />
              <Route path="/auth/google/callback" element={<OAuthCallback />} />
              
              {/* Protected routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/backups"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <BackupProgress />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Settings />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <AdminDashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/restore"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ContentRestore />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/audience"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <AudienceManager />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              {/* Catch all - redirect to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            
            {/* AI Chat Widget */}
            <ChatWidget />
            
            {/* Toast notifications */}
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="dark"
              style={{
                fontSize: '14px',
              }}
            />
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
  );
}

export default App; 